import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import Avatar from '../misc/Avatar';
import { DatePicker } from '@/components/form/widgets/DatePicker';
import { addDays } from 'date-fns';
import { useCalendarStore } from '@/hooks/use-calendar-store';
import { useTranslations } from 'next-intl';
import { useTimeOffs } from '@/hooks/swr/use-time-offs';
import { useResources } from '@/hooks/swr/use-resources';
import { ConfirmationButton } from '@/components/misc/ConfirmationButton';

const FormSchema = z.object({
  resourceId: z.string(),
  note: z.string(),
  range: z.tuple([z.date(), z.date()]),
});

export function TimeOffForm({
  id,
  afterSubmit,
  categoryId,
  resourceId: defaultResourceId,
  date,
}: {
  id?: string;
  afterSubmit: () => void;
  categoryId: string;
  resourceId?: string;
  date?: string;
}) {
  const { schedule } = useCalendarStore();
  const { timeOffs, add: addTimeOff, update: updateTimeOff, remove: removeTimeOff } = useTimeOffs({ scheduleId: schedule!.id, fetchAll: true });
  const { resources } = useResources({ scheduleId: schedule!.id });
  const t = useTranslations('calendar');

  const currentTimeOff = timeOffs.find((timeOff) => timeOff.id === id);

  // Apply default working hours when a date has no time set (midnight).
  const withDefaultTime = (value: Date, hours: number, minutes: number) => {
    if (value.getHours() === 0 && value.getMinutes() === 0) {
      value.setHours(hours, minutes);
    }
    return value;
  };

  const defaultStart = currentTimeOff
    ? new Date(currentTimeOff.start)
    : withDefaultTime(date ? new Date(date) : new Date(), 7, 0);
  const defaultEnd = currentTimeOff
    ? new Date(currentTimeOff.end)
    : withDefaultTime(date ? addDays(new Date(date), 1) : addDays(new Date(), 7), 16, 0);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      resourceId: currentTimeOff?.resourceId ?? defaultResourceId ?? '',
      note: currentTimeOff?.note ?? '',
      range: [defaultStart, defaultEnd],
    },
  });

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    let [start, end] = data.range;
    if (start > end) {
      [start, end] = [end, start];
    }
    data = { ...data, range: [start, end] };
    if (id) {
      await updateTimeOff({
        timeOffId: id,
        resourceId: data.resourceId,
        note: data.note,
        start: data.range[0],
        end: data.range[1],
        scheduleId: schedule!.id,
      });
    } else {
      await addTimeOff({
        resourceId: data.resourceId,
        note: data.note,
        start: data.range[0],
        end: data.range[1],
        scheduleId: schedule!.id,
      });
    }
    afterSubmit();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        {!defaultResourceId && (
          <FormField
            control={form.control}
            name="resourceId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('fields.resource')}</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t('placeholders.selectResource')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {resources
                      .filter((r) => r.categoryId === categoryId)
                      .map((r) => (
                        <SelectItem key={r.id} value={r.id!}>
                          <div className="flex w-full items-center gap-2">
                            <div>
                              <Avatar name={r.title!} />
                            </div>
                            <div>{r.title}</div>
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('fields.note')}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={2} className="min-h-0 resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="range"
          render={({ field }) => (
            <FormItem>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <FormLabel>{t('fields.startDate')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value[0]}
                      onChange={(from) => field.onChange([from, field.value[1]])}
                    />
                  </FormControl>
                </div>
                <div className="space-y-2">
                  <FormLabel>{t('fields.endDate')}</FormLabel>
                  <FormControl>
                    <DatePicker
                      value={field.value[1]}
                      onChange={(to) => field.onChange([field.value[0], to])}
                      align="end"
                    />
                  </FormControl>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="mt-2 flex items-center justify-between">
          {id && (
            <ConfirmationButton
              onConfirm={async () => { await removeTimeOff(id); afterSubmit(); }}
              trigger={
                <Button
                  type="button"
                  variant="outline"
                  disabled={form.formState.isSubmitting}
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  {t('contextMenu.delete')}
                </Button>
              }
            />
          )}
          <Button disabled={form.formState.isSubmitting} type="submit" className="ml-auto">
            {t('fields.submit')}
          </Button>
        </div>
      </form>
    </Form>
  );
}
